/** Platform bindings that sync intake answers to Venturu models. */
export const PLATFORM_BINDING_KEYS = [
  "contact.fullName",
  "contact.email",
  "contact.phone",
  "business.name",
  "business.businessTypeId",
  "deal.annualRevenue",
] as const;

export type PlatformBindingKey = (typeof PLATFORM_BINDING_KEYS)[number];

export const MANDATORY_CONTACT_FIELD_KEYS = [
  "full_name",
  "email",
  "phone",
] as const;

export type MandatoryContactFieldKey =
  (typeof MANDATORY_CONTACT_FIELD_KEYS)[number];

const PLATFORM_BINDING_SET = new Set<string>(PLATFORM_BINDING_KEYS);
const MANDATORY_CONTACT_SET = new Set<string>(MANDATORY_CONTACT_FIELD_KEYS);

export function isPlatformBoundBinding(
  bindingKey: string | null | undefined,
): bindingKey is PlatformBindingKey {
  return typeof bindingKey === "string" && PLATFORM_BINDING_SET.has(bindingKey);
}

/** Drop non-platform bindings so template questions stay editable. */
export function sanitizePlatformBinding(
  bindingKey: string | null | undefined,
): PlatformBindingKey | null {
  return isPlatformBoundBinding(bindingKey) ? bindingKey : null;
}

export function isMandatoryContactFieldKey(
  fieldKey: string | null | undefined,
): fieldKey is MandatoryContactFieldKey {
  return typeof fieldKey === "string" && MANDATORY_CONTACT_SET.has(fieldKey);
}

export function assertMandatoryContactFields(
  sections: Array<{
    fields: Array<{
      fieldKey: string;
      required?: boolean;
      kind?: string;
    }>;
  }>,
): void {
  const fields = sections.flatMap((section) => section.fields);
  for (const key of MANDATORY_CONTACT_FIELD_KEYS) {
    const field = fields.find((item) => item.fieldKey === key);
    if (!field) {
      throw new Error(
        `Intake forms must include the ${key.replace(/_/g, " ")} question.`,
      );
    }
    if (field.required !== true) {
      throw new Error(
        `The ${key.replace(/_/g, " ")} question must stay required.`,
      );
    }
  }
}
